import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog, SECONDARY_BUTTON } from '@/features/inventory/registerUi';
import { useWithdrawClearance } from './useClearance';

/**
 * SLICE 14c — WITHDRAW A CLEARANCE.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * 🔴 THE OUTCOME IS READ OFF THE RESPONSE, NEVER ASSUMED.
 * ─────────────────────────────────────────────────────────────────────────────
 * Withdrawing a human's attestation must not withdraw a machine proof. So this
 * does not announce "commanding has stopped" — it reads `commandVerifiedAt` off
 * the DELETE response and says the true thing:
 *   - null      → the attestation was the only thing enabling commands; commanding
 *                 has stopped.
 *   - non-null  → the device is separately HARDENED; commanding continues on the
 *                 machine proof, and the operator must be told so rather than
 *                 believing they just disabled it.
 *
 * A dialog that hard-coded "stopped" would be wrong in exactly the case where
 * being wrong is dangerous: an operator who thinks a vehicle can no longer be
 * commanded when it still can.
 */
export function WithdrawClearanceDialog({
  device,
  onClose,
}: {
  device: { deviceId: string; imei: string };
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const withdraw = useWithdrawClearance();
  const [error, setError] = useState<string | null>(null);
  // null until resolved; then the boolean is "still hardened after withdrawal".
  const [stillHardened, setStillHardened] = useState<boolean | null>(null);

  async function submit() {
    setError(null);
    try {
      const result = await withdraw.mutateAsync(device.deviceId);
      setStillHardened(result.commandVerifiedAt !== null);
    } catch {
      setError(t('clearance.withdraw.error'));
    }
  }

  const done = stillHardened !== null;

  return (
    <Dialog title={t('clearance.withdraw.title', { imei: device.imei })} onClose={onClose}>
      {done ? (
        <div className="grid gap-3">
          <p
            className={[
              'rounded-lg border p-3 text-[12.5px] text-raad-text',
              stillHardened
                ? 'border-raad-primary/40 bg-raad-primary/10'
                : 'border-raad-border bg-raad-bg/40',
            ].join(' ')}
          >
            {stillHardened
              ? t('clearance.withdraw.doneStillHardened', { imei: device.imei })
              : t('clearance.withdraw.doneStopped', { imei: device.imei })}
          </p>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="min-h-10 rounded-lg bg-raad-primary px-4 text-[13px] font-semibold text-raad-onprimary"
            >
              {t('clearance.withdraw.doneButton')}
            </button>
          </div>
        </div>
      ) : (
        <div className="grid gap-3">
          <p className="text-[13px]">{t('clearance.withdraw.intro')}</p>
          {error ? (
            <p
              role="alert"
              className="rounded-lg border border-raad-error/40 bg-raad-error/10 p-3 text-[12.5px] text-raad-text"
            >
              {error}
            </p>
          ) : null}
          <div className="mt-2 flex items-center justify-end gap-3">
            <button
              type="button"
              className={SECONDARY_BUTTON}
              onClick={onClose}
              disabled={withdraw.isPending}
            >
              {t('clearance.withdraw.back')}
            </button>
            <button
              type="button"
              onClick={() => void submit()}
              disabled={withdraw.isPending}
              className="min-h-10 rounded-lg bg-raad-error px-4 text-[13px] font-semibold text-white disabled:opacity-40"
            >
              {withdraw.isPending ? t('clearance.withdraw.working') : t('clearance.withdraw.confirm')}
            </button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
